import * as THREE from "three";
import { DrawingModes, ObstacleTypes, POLYGONS, VARS } from "../Global/variables";
import SegmentManager from "../Segments/SegmentManager";
import ObstacleManager from "../Obstacles/ObstacleManager";
import PanelManager from "../Panels/PanelManager";
import KeyEventListener from "./KeyEventListener";
import RulerManager from "../Ruler/RulerManager";
import { COLORS, OBSTACLE } from "../Global/materials";
import { panelOrientation, selection } from "./Helpers";
import { changeOrientation, removeGrid } from "../Panels/PanelFunctions";
import { findCenter } from "../Obstacles/Shapes";
import { deleteObstacle, selectObstacle } from "../Obstacles/ObstacleFunctions";
import { createSegmentPlane } from "../Global/functions";

export class MouseEventHandlers {
  constructor(SceneManager, currentDrawingModeRef, obstacleTypeRef, updateDrawingMode, polygons,
    setPolygons, setObstacles, setDeleteObstacle, setPanels, setDeletePanels, deletePanels, setDeleteConfirmationVisible, selectedIndices, setSelectedIndices,
    setPolygonDetails, modelMeshes, solarPanel, setSolarPanel, setShowPanelSettings, mouseDown, setMouseDown, panelList,
    arePanelsOK, setPanelsOK) {
    
    // Initialize properties for managing mouse interaction and drawing state
    this.mousePosition = new THREE.Vector2(); // Current mouse position in normalized device coordinates
    this.mouseDown = mouseDown; // State of mouse button (pressed/released)
    this.setMouseDown = setMouseDown; // Function to update mouseDown state
    this.mouseMoved = false; // Flag to track if mouse has moved
    this.sceneManager = SceneManager; // Scene manager to manage 3D scene
    this.currentDrawingMode = currentDrawingModeRef.current; // Current drawing mode (e.g., segment, obstacle)
    this.updateDrawingMode = updateDrawingMode; // Function to update the drawing mode
    this.Mesh = modelMeshes; // 3D model meshes to detect intersections
    this.isDrawingStarted = false; // Flag to track whether drawing has started
    this.shape = null; // Shape manager instance (e.g., for segments, obstacles)
    this.obstacleType = obstacleTypeRef.current; // Current obstacle type (e.g., rectangle, circle)
    this.start = new THREE.Vector2(); // Starting point for drawing shapes
    this.end = new THREE.Vector2(); // Ending point for drawing shapes
    this.polygonsState = polygons; // State of polygons (shapes being drawn)
    this.setPolygons = setPolygons; // Function to update polygons state
    this.setObstacles = setObstacles; // Function to update obstacles state
    this.setDeleteObstacle = setDeleteObstacle; // Function to delete obstacles
    this.setPanels = setPanels; // Function to update panels
    this.setDeletePanels = setDeletePanels; // Function to delete panels
    this.deletePanels = deletePanels; // Panel deletion logic
    this.selectedIndices = selectedIndices; // Selected indices for panels or obstacles
    this.setSelectedIndices = setSelectedIndices; // Function to update selected indices
    this.setPolygonDetails = setPolygonDetails; // Function to update polygon details
    this.setShowPanelSettings = setShowPanelSettings; // Function to show/hide panel settings
    this.solarPanel = solarPanel; // Solar panel state
    this.setSolarPanel = setSolarPanel; // Function to set solar panel state
    this.panelList = panelList; // List of panels in the scene
    this.panelTexture = null; // Panel texture (if any)
    this.arePanelsOK = arePanelsOK; // Whether panels are correctly positioned
    this.setPanelsOK = setPanelsOK; // Function to set panels OK state
    this.obsPos = null; // Position of obstacles for dragging

    // Handle shape completion (e.g., for segment drawing)
    const handleCompleteShape = () => {
      this.shape.completeShape(selectedIndices);
    }

    // Undo and redo functions for shape manipulation
    function undo() {
      this.shape.undo();
    }

    function redo() {
      this.shape.redo();
    }

    // Function to cancel segment drawing
    const cancelSegmentDrawing = () => {
      this.stopDrawing();
    }

    // Function to cancel drawing any shape (rectangle, circle, etc.)
    const cancelDrawing = () => {
      if (obstacleTypeRef.current === ObstacleTypes.RECTANGLE) {
        OBSTACLE.box.visible = false;
        this.stopDrawing();
      } else if (obstacleTypeRef.current === ObstacleTypes.CIRCLE) {
        OBSTACLE.circle.visible = false;
        this.stopDrawing();
      }
    }

    // Function to remove ruler when drawing
    function removeRuler() {
      if (this.shape) {
        this.shape.removeRuler();
      }
      this.stopDrawing();
    }

    // Create a new key listener or update the existing one
    this.keyListener = VARS.keyListener;
    if (this.keyListener === undefined || this.keyListener == null) {
      this.keyListener = new KeyEventListener(
        handleCompleteShape.bind(this),
        this.currentDrawingMode,
        undo.bind(this),
        redo.bind(this),
        setDeleteConfirmationVisible.bind(this),
        this.selectedIndices,
        cancelDrawing.bind(this),
        removeRuler.bind(this),
        cancelSegmentDrawing.bind(this),
        this.updateDrawingMode,
        this.setDeletePanels
      );
    } else {
      this.keyListener.completeShape = handleCompleteShape.bind(this);
      this.keyListener.currentDrawingMode = this.currentDrawingMode;
      this.keyListener.undo = undo.bind(this);
      this.keyListener.redo = redo.bind(this);
      this.keyListener.selectedindices = this.selectedIndices;
      this.keyListener.canceldrawing = cancelDrawing.bind(this);
      this.keyListener.removeRuler = removeRuler.bind(this);
      this.keyListener.cancelSegmentDrawing = cancelSegmentDrawing.bind(this);
    }
    VARS.keyListener = this.keyListener;
  }

  // Mouse down event handler
  handleMouseDown(event) {
    if (event.target === document.querySelector('canvas')) {
      this.setMouseDown(true); // Update mouse down state
      this.mouseDown = true; // Track mouse down state
      if (event.which === 1 || event.button === 0) {
        this.getMousePosition(event); // Get mouse position in normalized device coordinates
        let intersections = this.getIntersections(); // Get intersection points with 3D objects
        if (intersections.length > 0) {
          let point = intersections[0].point; // Get the first intersection point

          switch (this.currentDrawingMode) {
            // Handle drawing segments
            case DrawingModes.SEGMENT:
              if (!this.isDrawingStarted) {
                this.isDrawingStarted = true; // Mark drawing as started
                if (this.shape == null) {
                  this.shape = new SegmentManager(
                    this.sceneManager.scene,
                    this.stopDrawing,
                    this.setPolygons,
                    this.selectedIndices,
                    this.setSelectedIndices,
                    this.setPolygonDetails
                  );
                }
              }
              this.shape.handleMouseDown(point); // Handle mouse down for segment
              break;

            // Handle drawing obstacles
            case DrawingModes.OBSTACLE:
              if (!this.isDrawingStarted) {
                this.isDrawingStarted = true;
                this.shape = new ObstacleManager(
                  this.sceneManager.scene,
                  this.sceneManager.camera,
                  this.obstacleType,
                  this.stopDrawing,
                  this.setObstacles,
                  this.selectedIndices
                );
              }
              this.start = this.getPoints(this.start); // Set starting point for obstacle
              this.shape.handleMouseDown(this.start); // Handle mouse down for obstacle
              break;

            // Handle ruler drawing
            case DrawingModes.RULER:
              if (!this.isDrawingStarted) {
                this.isDrawingStarted = true;
                this.shape = new RulerManager(
                  this.sceneManager.scene,
                  this.stopDrawing
                );
              }
              this.shape.handleMouseDown(point); // Handle mouse down for ruler
              break;

            // Handle panel drawing
            case DrawingModes.PANEL:
              this.shape.handleMouseDown(this.setPanelsOK);
              break;

            // Handle none drawing mode (e.g., selection)
            case DrawingModes.NONE:
              selection(this.selectedIndices, this.setSelectedIndices, this.setPolygonDetails, this.mousePosition, this.sceneManager, this.setShowPanelSettings, this.setSolarPanel, this.polygonsState);              
              break;
          }
        }
      }
    }
  }

  // Mouse move event handler
  handleMouseMove(event) {
    this.getMousePosition(event); // Update mouse position
    let intersections = this.getIntersections(); // Get intersection points with 3D objects
    if (intersections.length > 0) {
      let point = intersections[0].point; // Get the first intersection point
      switch (this.currentDrawingMode) {
        // Handle moving segments
        case DrawingModes.SEGMENT:
          if (this.shape == null) {
            this.shape = new SegmentManager(
              this.sceneManager.scene,
              this.stopDrawing,
              this.setPolygons,
              this.selectedIndices,
              this.setSelectedIndices,
              this.setPolygonDetails
            );
          }
          this.shape.handleMouseMove(point); // Handle mouse move for segment
          break;

        // Handle moving obstacles
        case DrawingModes.OBSTACLE:
          if (this.isDrawingStarted) {
            this.end = this.getPoints(this.end); // Update endpoint for obstacle
            this.shape.handleMouseMove(this.end); // Handle mouse move for obstacle
          }
          break;

        // Handle moving ruler
        case DrawingModes.RULER:
          if (this.isDrawingStarted) {
            this.shape.handleMouseMove(point); // Handle mouse move for ruler
          }
          break;

        // Handle moving panels
        case DrawingModes.PANEL:
          if (!this.isDrawingStarted) {
            this.isDrawingStarted = true;
            this.shape = new PanelManager(
              this.sceneManager.scene,
              this.sceneManager.camera,
              this.selectedIndices.segmentIndex,
              this.solarPanel,
              this.setPanels,
              this.panelList,
              null,
              null,
              false
            );
          }
          this.shape.handleMouseMove(this.mousePosition); // Handle mouse move for panel
          break;

        // Handle selection and movement of existing panels or obstacles
        case DrawingModes.NONE:
          
        if (
          this.mouseDown &&
          event.which === 1 &&
          Array.isArray(this.selectedIndices.panelIndex) &&
          this.selectedIndices.panelIndex?.length > 0
        ) {            
          if (!this.isDrawingStarted) {
              this.isDrawingStarted = true;
              this.shape = [];
              this.positions = [];
              for (let index of this.selectedIndices.panelIndex) {
                const panel = POLYGONS[this.selectedIndices.segmentIndex].panels[index].panel;
                this.shape.push(new PanelManager(
                  this.sceneManager.scene,
                  this.sceneManager.camera,
                  this.selectedIndices.segmentIndex,
                  this.polygonsState[this.selectedIndices.segmentIndex].panels[index],
                  this.setPanels,
                  this.panelList,
                  panel,
                  panel.position.clone(),
                  true
                ));
                this.positions.push(this.getNDC(panel).sub(this.mousePosition));
              }
            }

            for (let i in this.shape) {
              this.shape[i].handleMouseMove(this.mousePosition.clone().add(this.positions[i]));
            }
            this.mouseMoved = true;
          }
          
          break;
        default:
          break;
      }
    }
  }

  handleMouseUp(event) {
    this.setMouseDown(false);
    this.mouseDown = false;
    if (this.mouseMoved && this.selectedIndices.panelIndex !== null && this.selectedIndices.panelIndex?.length > 0) {

      // set panel down
      for (let shape of this.shape) {
        shape.handleMouseUp(this.setPanelsOK, this.arePanelsOK);
      }
      this.stopDrawing();
      removeGrid();
      this.mouseMoved = false;
    }
    
  }

  handleDoubleClick(event) {
    if (event.button === 0) {
      this.getMousePosition(event);
      let intersections = this.getIntersections();
      if (intersections.length > 0) {
        if (this.currentDrawingMode === DrawingModes.NONE) {

          // change panel orientation on double click
          const panelIndex = panelOrientation(this.selectedIndices, this.mousePosition, this.sceneManager);
          if (panelIndex !== null) {
            let panelData = this.polygonsState[this.selectedIndices.segmentIndex].panels[panelIndex];
            let orient = null;
            if (panelData.orientation === 'Landscape') {
              orient = 'Portrait';
            } else {
              orient = 'Landscape';
            }
            const solarPanel = { ...panelData, orientation: orient };
            let panel = POLYGONS[this.selectedIndices.segmentIndex].panels[panelIndex].panel;
            changeOrientation(
              this.sceneManager.scene,
              this.sceneManager.camera,
              this.selectedIndices.segmentIndex,
              solarPanel,
              this.setPanels,
              this.panelList,
              panel,
              panelIndex,
              this.deletePanels,
              this.setPanelsOK,
              this.arePanelsOK
            );
    }
        }
      }
    }
  }

  // Stop drawing process
  stopDrawing = () => {
    this.isDrawingStarted = false;
    this.updateDrawingMode(DrawingModes.NONE);
  }

  // Get normalized mouse position in device coordinates
  // Get normalized mouse position in device coordinates
getMousePosition(event) {
  this.mousePosition.x = (event.clientX / window.innerWidth) * 2 - 1;
  const offsetY = event.clientY - 92.8;
  this.mousePosition.y = -(offsetY / this.sceneManager.height) * 2 + 1;

  //  expose latest mouse position for paste logic
  VARS.mouseNDC = {
    x: this.mousePosition.x,
    y: this.mousePosition.y,
  };
}

  // get normalized device coordinates
  getNDC(object) {
    let position = object.getWorldPosition(new THREE.Vector3());
    let projection = position.clone().project(this.sceneManager.camera);
    return new THREE.Vector2(projection.x, projection.y);
  }

  // Get a point position for drawing shapes
  getPoints(point) {
    // since it doesn't work with this.mousePosition we use this
    point.x = this.mousePosition.x;
    point.y = this.mousePosition.y;
    return point;
  }

  // Get intersection points with 3D objects
  getIntersections() {
    let rayCaster = new THREE.Raycaster();
    rayCaster.setFromCamera(this.mousePosition, this.sceneManager.camera);
    return rayCaster.intersectObjects(this.Mesh, false);
  }
}